import {
  resolveLocalWebLlmModel,
  type LocalWebLlmModelInput,
  type ResolvedLocalWebLlmModel,
} from "@/llm/webllmModelRegistry";

export type TinyLlamaInitOptions = {
  model?: LocalWebLlmModelInput;
  modelId?: string;
  onProgress?: (loaded: number, total: number, stage?: string) => void;
  appConfig?: any;
};

export type GenerateOptions = {
  prompt: string;
  maxTokens?: number;
  temperature?: number;
  topP?: number;
  frequencyPenalty?: number;
  presencePenalty?: number;
  stopSequences?: string[];
  onToken?: (token: string) => void;
  onDone?: (full: string) => void;
};

export type WebGpuInfo = {
  adapterName?: string;
  limits?: any;
  shaderF16Supported?: boolean;
};

export class TinyLlamaWebGpuRuntime {
  private engine: any | null = null;
  private webllm: any | null = null;
  private currentModel: ResolvedLocalWebLlmModel = resolveLocalWebLlmModel("Llama-3.2-1B-Instruct-q4f32_1-MLC");

  constructor(model?: LocalWebLlmModelInput) {
    if (model) {
      this.currentModel = resolveLocalWebLlmModel(model);
    }
  }

  static isWebGpuSupported(): boolean {
    return typeof navigator !== "undefined" && !!(navigator as any).gpu;
  }

  static async getWebGpuInfo(): Promise<WebGpuInfo | null> {
    if (!TinyLlamaWebGpuRuntime.isWebGpuSupported()) return null;
    try {
      const isWindows = typeof navigator !== 'undefined' && /win/i.test(navigator.platform);
      const adapter = await (navigator as any).gpu.requestAdapter(
        isWindows ? undefined : { powerPreference: "high-performance" }
      );
      if (!adapter) return null;
      return {
        adapterName: (adapter as any).name || "Detected",
        limits: (adapter as any).limits,
        shaderF16Supported: typeof adapter.features?.has === 'function' ? adapter.features.has('shader-f16') : undefined,
      };
    } catch {
      return null;
    }
  }

  getResolvedModel() {
    return this.currentModel;
  }

  private buildAppConfig(model: ResolvedLocalWebLlmModel, modelLib: string, baseAppConfig?: any) {
    const modelEntry = {
      model: model.modelUrl,
      model_id: model.engineModelId,
      low_resource_required: true,
      model_lib: modelLib,
      overrides: model.contextWindowSize
        ? {
            context_window_size: model.contextWindowSize,
          }
        : undefined,
    };

    return {
      ...baseAppConfig,
      model_list: [modelEntry],
      useIndexedDBCache: baseAppConfig?.useIndexedDBCache ?? false,
    };
  }

  async init(options?: TinyLlamaInitOptions): Promise<void> {
    if (!TinyLlamaWebGpuRuntime.isWebGpuSupported()) {
      throw new Error("WebGPU is not supported in this browser.");
    }

    const webGpuInfo = await TinyLlamaWebGpuRuntime.getWebGpuInfo();
    const model = resolveLocalWebLlmModel(
      options?.model ?? options?.modelId ?? this.currentModel.modelId,
      { preferF32: webGpuInfo?.shaderF16Supported === false }
    );
    const baseAppConfig = options?.appConfig ?? {};

    if (!this.webllm) {
      this.webllm = await import("@mlc-ai/web-llm/runtime");
    }

    await this.dispose();

    let lastError: unknown = null;

    for (const modelLib of model.modelLibCandidates) {
      const appConfig = this.buildAppConfig(model, modelLib, baseAppConfig);
      (globalThis as any).mlc_app_config = appConfig;

      const engineConfig: any = {
        initProgressCallback: (p: any) => {
          if (options?.onProgress) {
            const loaded = Math.floor((p.progress || 0) * 100);
            options.onProgress(loaded, 100, p.text);
          }
        },
        use_web_worker: false,
        appConfig,
      };

      try {
        this.engine = await this.webllm.CreateMLCEngine(model.engineModelId, engineConfig);
        this.currentModel = model;
        return;
      } catch (error) {
        lastError = error;
        this.engine = null;
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new Error(`Failed to initialize local model ${model.engineModelId}.`);
  }

  async generate(opts: GenerateOptions): Promise<string> {
    if (!this.engine) {
      throw new Error("Engine not initialized");
    }

    const {
      prompt,
      maxTokens = 256,
      temperature = 0.7,
      topP = 0.95,
      frequencyPenalty,
      presencePenalty,
      stopSequences,
      onToken,
      onDone,
    } = opts;

    const messages = [{ role: "user", content: prompt }];
    let full = "";

    const stream = await this.engine.chat.completions.create({
      messages,
      stream: true,
      temperature,
      top_p: topP,
      max_tokens: maxTokens,
      frequency_penalty: frequencyPenalty,
      presence_penalty: presencePenalty,
      stop: stopSequences,
    });

    for await (const chunk of stream) {
      const token = chunk.choices?.[0]?.delta?.content || "";
      if (token) {
        full += token;
        onToken?.(token);
      }
    }

    onDone?.(full);
    return full;
  }

  async dispose(): Promise<void> {
    if (this.engine) {
      try {
        await this.engine.unload?.();
      } catch {
        // noop
      }
      this.engine = null;
    }
  }
}

export default TinyLlamaWebGpuRuntime;